import { refundMethods as allowedRefundMethods } from '@reveni-io/reveni-ui'

import { currencies, extraCreditFeeType } from '../constants'
import { getTextForWhenIssueRefund } from '../helpers'

export const isBankTransferAvailable = refundMethods => {
  return !!refundMethods.find(rm => rm.name === allowedRefundMethods.bankTransfer)
}

export const isOriginalPaymentMethodAvailable = refundMethods => {
  return !!refundMethods?.find(rm => rm.name === allowedRefundMethods.originalPaymentMethod)
}

export const isStoreCreditAvailable = refundMethods => {
  return !!refundMethods.find(rm => rm.name === allowedRefundMethods.storeCredit)
}

const isGiftCardAvailable = refundMethods => {
  return !!refundMethods.find(rm => rm.name === allowedRefundMethods.giftCard)
}

const filterAvailableRefundMethods = (option, refundMethods) => {
  const isAvailable = {
    [allowedRefundMethods.bankTransfer]: isBankTransferAvailable,
    [allowedRefundMethods.originalPaymentMethod]: isOriginalPaymentMethodAvailable,
    [allowedRefundMethods.storeCredit]: isStoreCreditAvailable,
    [allowedRefundMethods.giftCard]: isGiftCardAvailable,
  }

  return isAvailable[option.value](refundMethods)
}

const mapOptionWithLabelAndHelperText = option => {
  if (option.is_instant) option.helperText = option.instantHelperText

  if (option.feeAmountHelperText && option.feeAmount) option.helperText = option.feeAmountHelperText

  return option
}

export const sortRefundMethods = (current, next) => {
  const currentInstant = current.is_instant
  const currentPosition = current.position
  const nextInstant = next.is_instant
  const nextPosition = next.position

  if (currentInstant && !nextInstant) return -1
  if (!currentInstant && nextInstant) return 1
  if (currentPosition < nextPosition) return -1
  if (currentPosition > nextPosition) return 1

  return 0
}

const setValuesFromAllowedRefundMethodsToRefundMethodOptions = (
  option,
  refundMethods,
  extraStoreCredit,
  extraGiftCardCredit
) => {
  const refundMethod = refundMethods.find(rm => rm.name === option.value)
  option.is_instant = refundMethod.is_instant
  option.feeAmount = refundMethod.fee_amount
  option.refundAmount = refundMethod.refund_amount
  option.position = refundMethod.position

  if (refundMethod.name === allowedRefundMethods.storeCredit) {
    option.extraCredit = extraStoreCredit
  }

  if (refundMethod.name === allowedRefundMethods.giftCard) {
    option.extraCredit = extraGiftCardCredit
  }

  return option
}

export const getOptions = ({
  options,
  t,
  storeName,
  refundMethods,
  currency,
  language,
  issueRefundAtStatus,
  isCancellationsPortal,
}) => {
  const refundMethodsCustomNames = getRefundMethodsCustomNames(refundMethods)
  const extraStoreCredit = getExtraCredit(refundMethods, currency, language, allowedRefundMethods.storeCredit)
  const textForWhenIssueRefund = getTextForWhenIssueRefund(t, issueRefundAtStatus)
  const extraGiftCardCredit = getExtraCredit(refundMethods, currency, language, allowedRefundMethods.giftCard)

  return options({
    t,
    storeName,
    extraStoreCredit,
    textForWhenIssueRefund,
    refundMethodsCustomNames,
    isCancellationsPortal,
    extraGiftCardCredit,
  })
    .filter(option => filterAvailableRefundMethods(option, refundMethods))
    .map(option =>
      setValuesFromAllowedRefundMethodsToRefundMethodOptions(
        option,
        refundMethods,
        extraStoreCredit,
        extraGiftCardCredit
      )
    )
    .map(mapOptionWithLabelAndHelperText)
    .sort((current, prev) => sortRefundMethods(current, prev))
}

export const getExtraCredit = (refundMethods, currency = currencies.EUR, language, refundMethodName) => {
  const refundMethodWithExtraCredit = refundMethods.find(rm => rm.name === refundMethodName)

  let value = ''

  if (refundMethodWithExtraCredit?.extra_value && Number(refundMethodWithExtraCredit.extra_value) > 0) {
    if (refundMethodWithExtraCredit.extra_value_type === extraCreditFeeType.fixedAmount) {
      value = new Intl.NumberFormat(language, { style: 'currency', currency }).format(
        refundMethodWithExtraCredit.extra_value
      )
    } else if (refundMethodWithExtraCredit.extra_value_type === extraCreditFeeType.percentage) {
      value = `+${parseFloat(refundMethodWithExtraCredit.extra_value)}%`
    } else if (refundMethodWithExtraCredit.extra_value_type === extraCreditFeeType.deliveryMethodFee) {
      value = new Intl.NumberFormat(language, { style: 'currency', currency }).format(
        refundMethodWithExtraCredit.extra_value
      )
    }

    return `${value}`
  }
}

export const isRefundMethodInstant = (refundMethod, refundMethods) => {
  const selectedRefundMethod = refundMethods?.find(rm => rm.name === refundMethod)

  return selectedRefundMethod?.is_instant || false
}

export const getRefundMethodsCustomNames = refundMethods => {
  return refundMethods.reduce((acc, refundMethod) => {
    acc[refundMethod.name] = refundMethod.display
    return acc
  }, {})
}
